import type { DetectionProfile } from '@groupe-reca/contracts'
import type { FieldMissionRepository } from '../../application/FieldMissionRepository'
import {
  MissionCommandError,
  type FieldItemStatus,
  type FieldMissionHeader,
  type FieldMissionItem,
  type MissionDetail,
  type MissionListRow,
} from '../../domain/fieldMission'

// Données de démonstration du mode fixtures (développement et tests) : jamais montrées comme des données réelles.

const minute = 60_000
// Relatif à maintenant : les durées et « reçue il y a… » restent vraisemblables en démonstration.
const base = Date.now() - 45 * minute

function at(minutes: number): string {
  return new Date(base + minutes * minute).toISOString()
}

function uuid(prefix: string, index: number): string {
  return `${prefix}-0000-4000-a000-${String(index).padStart(12, '0')}`
}

const streets = [
  'Rue Labelle',
  'Rue Saint-Georges',
  'Boulevard des Hauteurs',
  'Rue de Martigny',
  'Rue Castonguay',
]
const clients = [
  'Claire Bélanger',
  'Jacques Gagnon',
  'Famille Roy',
  'Nadia Tremblay',
  'Pierre Lavoie',
  'Sophie Côté',
]

interface ItemSeed {
  status: FieldItemStatus
  minutes?: number
  problem?: 'VEHICLE_IN_DRIVEWAY' | 'ACCESS_BLOCKED' | 'OBSTACLE' | 'OTHER'
  resolved?: boolean
  located?: boolean
}

function buildItems(missionIndex: number, seeds: ItemSeed[]): FieldMissionItem[] {
  const missionId = uuid('f0000000', missionIndex)
  return seeds.map((seed, index) => {
    const id = uuid(`f${String(missionIndex)}000001`, index + 1)
    const events: FieldMissionItem['events'] = []
    if (seed.problem) {
      events.push({
        id: uuid(`f${String(missionIndex)}000002`, index + 1),
        kind: 'PROBLEM_REPORTED',
        fromStatus: 'WAITING',
        toStatus: 'PROBLEM',
        reasonCode: seed.problem,
        reasonText: seed.problem === 'OTHER' ? 'Chien en liberté dans la cour' : null,
        note: seed.problem === 'VEHICLE_IN_DRIVEWAY' ? 'Camionnette grise' : null,
        occurredAt: at((seed.minutes ?? 10) - 5),
        actorName: 'Olivier Gauthier',
      })
    }
    if (seed.status === 'COMPLETED') {
      events.push({
        id: uuid(`f${String(missionIndex)}000003`, index + 1),
        kind: seed.resolved ? 'PROBLEM_RESOLVED' : 'COMPLETED',
        fromStatus: seed.resolved ? 'PROBLEM' : 'WAITING',
        toStatus: 'COMPLETED',
        reasonCode: null,
        reasonText: null,
        note: null,
        occurredAt: at(seed.minutes ?? 10),
        actorName: 'Olivier Gauthier',
      })
    }
    const located = seed.located ?? true
    return {
      id,
      missionId,
      order: index + 1,
      status: seed.status,
      itemVersion: events.length + 1,
      serviceLocationId: uuid('f9000000', missionIndex * 100 + index),
      contractId: uuid('f8000000', missionIndex * 100 + index),
      snapshot: {
        addressLine1: `${String(120 + index * 14)} ${streets[index % streets.length] ?? ''}`,
        addressLine2: index === 2 ? 'Unité 3' : null,
        city: 'Saint-Jérôme',
        postalCode: 'J7Z 1A1',
        formattedAddress: null,
        clientName: clients[index % clients.length] ?? null,
        contractNumber: `CTR-26${String(1000 + missionIndex * 100 + index)}`,
        latitude: located ? 45.7783 + index * 0.0012 : null,
        longitude: located ? -74.0035 - index * 0.0015 : null,
        routeOrder: index + 1,
      },
      completedAt: seed.status === 'COMPLETED' ? at(seed.minutes ?? 10) : null,
      clearedAt: null,
      clearedReason: null,
      events,
    }
  })
}

function progress(items: FieldMissionItem[]) {
  return {
    total: items.length,
    completed: items.filter((item) => item.status === 'COMPLETED').length,
    problem: items.filter((item) => item.status === 'PROBLEM').length,
    waiting: items.filter((item) => item.status === 'WAITING').length,
  }
}

function header(
  index: number,
  values: Partial<FieldMissionHeader> & Pick<FieldMissionHeader, 'status' | 'legacyStatus'>,
  items: FieldMissionItem[],
): FieldMissionHeader {
  return {
    id: uuid('f0000000', index),
    number: 40 + index,
    organizationId: null,
    season: '2026-2027',
    routeId: uuid('f1000000', index),
    routeName: 'Route Bleu',
    routeColor: '#3B82F6',
    routeVersion: 7,
    routeDescription: null,
    operatorId: uuid('f2000000', index),
    operatorName: 'Olivier Gauthier',
    equipmentId: uuid('f3000000', index),
    equipmentLabel: 'T-04 — Kubota M7060',
    startedAt: at(0),
    endedAt: null,
    pausedAt: null,
    startEngineHours: 1520.5,
    endEngineHours: null,
    endSummary: null,
    cancelledReason: null,
    pausedSeconds: 0,
    ...values,
    progress: progress(items),
  }
}

function seedDetails(): MissionDetail[] {
  const firstItems = buildItems(1, [
    { status: 'COMPLETED', minutes: 8 },
    { status: 'COMPLETED', minutes: 15 },
    { status: 'PROBLEM', minutes: 22, problem: 'VEHICLE_IN_DRIVEWAY' },
    { status: 'WAITING' },
    { status: 'WAITING', located: false },
    { status: 'WAITING' },
  ])
  const pausedItems = buildItems(2, [
    { status: 'COMPLETED', minutes: 12 },
    { status: 'WAITING' },
    { status: 'WAITING' },
  ])
  const doneItems = buildItems(3, [
    { status: 'COMPLETED', minutes: 9 },
    { status: 'COMPLETED', minutes: 20, problem: 'ACCESS_BLOCKED', resolved: true },
    { status: 'PROBLEM', minutes: 30, problem: 'OTHER' },
    { status: 'WAITING' },
  ])
  const missions: [FieldMissionHeader, FieldMissionItem[]][] = [
    [header(1, { status: 'IN_PROGRESS', legacyStatus: 'en_cours' }, firstItems), firstItems],
    [
      header(
        2,
        {
          status: 'PAUSED',
          legacyStatus: 'en_cours',
          routeName: 'Route Vert',
          routeColor: '#22C55E',
          operatorName: 'Maude Pelletier',
          equipmentLabel: 'T-02 — John Deere 5075E',
          startedAt: at(10),
          pausedAt: at(40),
          pausedSeconds: 900,
          startEngineHours: 842.25,
        },
        pausedItems,
      ),
      pausedItems,
    ],
    [
      header(
        3,
        {
          status: 'COMPLETED',
          legacyStatus: 'terminee_avec_anomalies',
          routeName: 'Route Ambre',
          routeColor: '#F59E0B',
          operatorName: 'Samuel Ouellet',
          equipmentLabel: 'T-01 — Kubota L4760',
          startedAt: at(-300),
          endedAt: at(-120),
          startEngineHours: 2310,
          endEngineHours: 2313.4,
          pausedSeconds: 1200,
          endSummary: { total: 4, completed: 2, problem: 1, waiting: 1, unresolved: true },
        },
        doneItems,
      ),
      doneItems,
    ],
  ]
  return missions.map(([mission, items]) => ({
    mission,
    items,
    pauses:
      mission.status === 'PAUSED'
        ? [{ startedAt: at(40), endedAt: null }]
        : mission.pausedSeconds > 0
          ? [{ startedAt: at(-240), endedAt: at(-220) }]
          : [],
    readings: [
      { kind: 'START', value: mission.startEngineHours, recordedAt: mission.startedAt, reason: null },
      ...(mission.endEngineHours !== null && mission.endedAt
        ? [{ kind: 'END' as const, value: mission.endEngineHours, recordedAt: mission.endedAt, reason: null }]
        : []),
    ],
    events: [
      { type: 'mission_creee', payload: { source: 'OPERATOR_FIELD_V1' }, createdAt: mission.startedAt },
      { type: 'mission_debutee', payload: { source: 'OPERATOR_FIELD_V1' }, createdAt: mission.startedAt },
    ],
    gps: {
      pointCount: mission.status === 'COMPLETED' ? 341 : 64,
      lastPosition:
        mission.status === 'IN_PROGRESS'
          ? { capturedAt: at(31), receivedAt: at(31), latitude: 45.7801, longitude: -74.0061, accuracy: 6 }
          : null,
      lastReceivedAt: mission.status === 'IN_PROGRESS' ? at(31) : mission.endedAt,
      gaps:
        mission.status === 'IN_PROGRESS'
          ? [{ kind: 'APP_HIDDEN', startedAt: at(18), endedAt: at(20), receivedAt: at(21) }]
          : [],
    },
  }))
}

function row(detail: MissionDetail): MissionListRow {
  const { mission } = detail
  return {
    id: mission.id,
    number: mission.number,
    status: mission.status,
    legacyStatus: mission.legacyStatus,
    operatorName: mission.operatorName,
    equipmentLabel: mission.equipmentLabel,
    routeName: mission.routeName,
    routeColor: mission.routeColor,
    startedAt: mission.startedAt,
    endedAt: mission.endedAt,
    progress: mission.progress,
    lastPositionReceivedAt: detail.gps.lastReceivedAt,
  }
}

function normalize(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()
}

export function createFixtureFieldMissionRepository(): FieldMissionRepository {
  const details = seedDetails()
  const find = (missionId: string) => details.find((detail) => detail.mission.id === missionId)
  // Aucun profil au départ, comme en production : détection OFF tant que le bureau n'en publie pas.
  const profiles: DetectionProfile[] = []

  return {
    dashboard() {
      const missions = details.map((detail) => detail.mission)
      return Promise.resolve({
        inProgress: missions.filter((mission) => mission.status === 'IN_PROGRESS').length,
        paused: missions.filter((mission) => mission.status === 'PAUSED').length,
        completedToday: missions.filter((mission) => mission.status === 'COMPLETED').length,
        completedTodayWithUnresolved: missions.filter(
          (mission) => mission.status === 'COMPLETED' && mission.legacyStatus === 'terminee_avec_anomalies',
        ).length,
        unresolvedProblems: details
          .flatMap((detail) => detail.items)
          .filter((item) => item.status === 'PROBLEM' && !item.clearedAt).length,
        today: new Date().toISOString().slice(0, 10),
      })
    },
    list(filters) {
      const search = normalize(filters.search.trim())
      const rows = details
        .map(row)
        .filter((candidate) =>
          filters.tab === 'current'
            ? candidate.status === 'IN_PROGRESS' || candidate.status === 'PAUSED'
            : candidate.status === 'COMPLETED' || candidate.status === 'CANCELLED',
        )
        .filter((candidate) => {
          if (!filters.status) return true
          if (filters.status === 'COMPLETED_UNRESOLVED')
            return candidate.status === 'COMPLETED' && candidate.legacyStatus === 'terminee_avec_anomalies'
          if (filters.status === 'TO_REVIEW')
            return (
              candidate.status === 'COMPLETED' && candidate.progress.waiting + candidate.progress.problem > 0
            )
          if (filters.status === 'COMPLETED')
            return candidate.status === 'COMPLETED' && candidate.legacyStatus === 'terminee'
          return candidate.status === filters.status
        })
        .filter(
          (candidate) =>
            !search ||
            normalize(
              [candidate.operatorName, candidate.routeName, candidate.equipmentLabel].join(' '),
            ).includes(search),
        )
      const start = (filters.page - 1) * filters.pageSize
      return Promise.resolve({
        rows: rows.slice(start, start + filters.pageSize),
        total: rows.length,
        page: filters.page,
        pageSize: filters.pageSize,
      })
    },
    detail(missionId) {
      return Promise.resolve(find(missionId) ?? null)
    },
    track(missionId) {
      return Promise.resolve({ missionId, points: [], pauses: [], gaps: [] })
    },
    cancel(command) {
      const detail = find(command.missionId)
      if (!detail) return Promise.reject(new MissionCommandError('MISSION_NOT_FOUND'))
      if (detail.mission.status !== 'IN_PROGRESS' && detail.mission.status !== 'PAUSED')
        return Promise.reject(new MissionCommandError('MISSION_NOT_OPEN'))
      detail.mission = {
        ...detail.mission,
        status: 'CANCELLED',
        legacyStatus: 'annulee',
        cancelledReason: command.reason,
        endedAt: new Date().toISOString(),
      }
      return Promise.resolve()
    },
    correctHourMeter() {
      return Promise.resolve()
    },
    clearUnresolvedItem(command) {
      for (const detail of details) {
        const item = detail.items.find((candidate) => candidate.id === command.itemId)
        if (!item) continue
        if (detail.mission.status === 'IN_PROGRESS' || detail.mission.status === 'PAUSED')
          return Promise.reject(new MissionCommandError('MISSION_STILL_OPEN'))
        item.clearedAt = new Date().toISOString()
        item.clearedReason = command.reason
        return Promise.resolve()
      }
      return Promise.reject(new MissionCommandError('MISSION_NOT_FOUND'))
    },
    detectionProfiles() {
      return Promise.resolve({ active: profiles[0] ?? null, history: [...profiles] })
    },
    publishDetectionProfile(command) {
      const activeVersion = profiles[0]?.version ?? null
      if (activeVersion !== command.expectedVersion)
        return Promise.reject(new MissionCommandError('MISSION_DETECTION_PROFILE_CONFLICT'))
      const reason = command.reason?.trim() ? command.reason.trim() : null
      if (command.mode === 'AUTO' && (!command.certified || !reason))
        return Promise.reject(new MissionCommandError('MISSION_DETECTION_AUTO_REQUIRES_VALIDATION'))
      const version = (activeVersion ?? 0) + 1
      const profile: DetectionProfile = {
        id: uuid('d0000000', version),
        version,
        label: command.label.trim(),
        mode: command.mode,
        certified: command.certified,
        parameters: { ...command.parameters },
        reason,
        createdAt: new Date().toISOString(),
        createdByName: 'Administrateur démo',
      }
      profiles.unshift(profile)
      return Promise.resolve(profile)
    },
  }
}
